import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Package, Truck, FileDown, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Link } from "react-router-dom";

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-700",
  processing: "bg-blue-100 text-blue-700",
  shipped: "bg-purple-100 text-purple-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-destructive/10 text-destructive",
  refunded: "bg-muted text-muted-foreground",
};

const AdminRoyalMail = () => {
  const qc = useQueryClient();
  const [rmLoading, setRmLoading] = useState<Record<string, string | null>>({});
  
  const [filter, setFilter] = useState<"all" | "pending_shipment" | "shipped">("all");
  const [search, setSearch] = useState("");

  const { data: orders, isLoading } = useQuery({
    queryKey: ["admin-royal-mail-orders"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id, customer_name, customer_email, shipping_address, status, total, tracking_number, created_at")
        .in("status", ["processing", "shipped", "completed"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const filteredOrders = (orders || []).filter((o) => {
    if (filter === "pending_shipment") return o.status === "processing" && !o.tracking_number;
    if (filter === "shipped") return o.status === "shipped" || !!o.tracking_number;
    return true;
  }).filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      o.customer_name?.toLowerCase().includes(s) ||
      o.customer_email?.toLowerCase().includes(s) ||
      o.id.toLowerCase().includes(s) ||
      o.tracking_number?.toLowerCase().includes(s)
    );
  });

  const callRoyalMail = async (orderId: string, action: string) => {
    setRmLoading((prev) => ({ ...prev, [orderId]: action }));
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (action === "get_label") {
        const token = session?.access_token;
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/royal-mail`;
        const labelRes = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ action: "get_label", orderId }),
        });
        if (!labelRes.ok) {
          const err = await labelRes.json();
          throw new Error(err.error || "Failed to fetch label");
        }

        const contentType = labelRes.headers.get("Content-Type") || "";

        if (contentType.includes("application/json")) {
          // Label download not available — but tracking was fetched
          const jsonData = await labelRes.json();
          if (jsonData.trackingNumber) {
            toast.success(`Tracking number saved: ${jsonData.trackingNumber}`);
          }
          if (jsonData.labelUnavailable) {
            toast.info("Print your label in Click & Drop — API label download isn't available for your account.", { duration: 6000 });
            window.open("https://parcel.royalmail.com/", "_blank");
          }
          qc.invalidateQueries({ queryKey: ["admin-royal-mail-orders"] });
          return;
        }

        // PDF label returned — open it
        const blob = await labelRes.blob();
        window.open(URL.createObjectURL(blob), "_blank");
        toast.success("Label opened");
        qc.invalidateQueries({ queryKey: ["admin-royal-mail-orders"] });
        return;
      }

      const res = await supabase.functions.invoke("royal-mail", {
        body: { action, orderId },
      });

      if (res.error) throw new Error(res.error.message);

      if (action === "create_order") {
        const tn = res.data?.trackingNumber;
        const emailSent = res.data?.emailSent;
        const msg = tn ? `Shipment created! Tracking: ${tn}` : "Order sent to Royal Mail";
        toast.success(emailSent ? `${msg} — tracking email sent to customer` : msg);
        qc.invalidateQueries({ queryKey: ["admin-royal-mail-orders"] });
      }
    } catch (e: any) {
      toast.error(e.message || "Royal Mail action failed");
    } finally {
      setRmLoading((prev) => ({ ...prev, [orderId]: null }));
    }
  };

  const bulkCreateShipments = async () => {
    const pending = filteredOrders.filter((o) => !o.tracking_number);
    if (!pending.length) {
      toast.info("No orders pending shipment");
      return;
    }
    for (const order of pending) {
      await callRoyalMail(order.id, "create_order");
    }
  };

  const pendingCount = (orders || []).filter((o) => !o.tracking_number).length;
  const shippedCount = (orders || []).filter((o) => !!o.tracking_number).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6" /> Royal Mail
          </h1>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Manage shipments, labels, and tracking
          </p>
        </div>
        <Button
          onClick={bulkCreateShipments}
          disabled={!pendingCount}
          className="gap-2"
        >
          <Truck className="w-4 h-4" />
          Create All Shipments ({pendingCount})
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-card rounded-lg border border-border p-4 text-center">
          <p className="text-2xl font-display font-bold">{(orders || []).length}</p>
          <p className="text-xs text-muted-foreground font-body">Total Orders</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-4 text-center">
          <p className="text-2xl font-display font-bold text-yellow-600">{pendingCount}</p>
          <p className="text-xs text-muted-foreground font-body">Pending Shipment</p>
        </div>
        <div className="bg-card rounded-lg border border-border p-4 text-center">
          <p className="text-2xl font-display font-bold text-green-600">{shippedCount}</p>
          <p className="text-xs text-muted-foreground font-body">Shipped</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, email, order ID, or tracking…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-md border border-input bg-background text-sm font-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <div className="flex gap-1">
          {(["all", "pending_shipment", "shipped"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-md text-xs font-medium font-body transition-colors ${
                filter === f
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-muted-foreground hover:bg-secondary/80"
              }`}
            >
              {f === "all" ? "All" : f === "pending_shipment" ? "Pending" : "Shipped"}
            </button>
          ))}
        </div>
      </div>

      {/* Orders */}
      {isLoading ? (
        <div className="text-muted-foreground text-center py-12">Loading orders…</div>
      ) : !filteredOrders.length ? (
        <div className="text-muted-foreground text-center py-12 bg-card rounded-lg border border-border">
          No orders found
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => (
            <div key={order.id} className="bg-card rounded-lg border border-border p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Link
                      to={`/admin/orders/${order.id}`}
                      className="font-mono text-sm font-medium hover:text-primary transition-colors"
                    >
                      #{order.id.slice(0, 8)}
                    </Link>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[order.status] || "bg-muted text-muted-foreground"}`}>
                      {order.status}
                    </span>
                  </div>
                  <p className="text-sm font-body">
                    {order.customer_name || "Unknown"} · £{order.total.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground font-body">
                    {order.shipping_address || "No address"} · {format(new Date(order.created_at), "dd MMM yyyy")}
                  </p>
                </div>

                {order.tracking_number && (
                  <div className="px-3 py-1.5 bg-secondary/50 rounded text-xs font-mono">
                    {order.tracking_number}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {order.status === "processing" && !order.tracking_number ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={rmLoading[order.id] !== null && rmLoading[order.id] !== undefined}
                    onClick={() => callRoyalMail(order.id, "create_order")}
                  >
                    <Truck className="w-3.5 h-3.5" />
                    {rmLoading[order.id] === "create_order" ? "Creating…" : "Create Shipment"}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={!!rmLoading[order.id]}
                      onClick={() => callRoyalMail(order.id, "get_label")}
                    >
                      <FileDown className="w-3.5 h-3.5" />
                      {rmLoading[order.id] === "get_label" ? "Fetching…" : "Label"}
                    </Button>
                    {order.tracking_number && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        asChild
                      >
                        <a
                          href={`https://www.royalmail.com/track-your-item#/tracking-results/${order.tracking_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Track
                        </a>
                      </Button>
                    )}
                  </>
                )}
              </div>

            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminRoyalMail;
